import { useState, useMemo, useRef, useEffect } from "react";
import { Dialog } from "@headlessui/react";
import { NavLink, Link, useLocation } from "react-router-dom";
import {
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import { FaLocationDot, FaPhone } from "react-icons/fa6";
import { TbMailFilled } from "react-icons/tb";
import icon from "../assets/pad-pro-icon.png";

const navigation = [
  { name: "Home ", path: "/", external: false },
  { name: "Services ", path: "/services", external: false },
  { name: "About Us ", path: "/about-us", external: false },
  {
    name: "More",
    subItems: [
      { name: "Events", path: "/events", external: false },
      { name: "Facilities", path: "/facilities", external: false },
      {
        name: "Virtual Programming",
        path: "https://www.youtube.com/channel/UCwlvTf4r-Qx4GB3caNw0AEg",
        external: true,
      },
    ],
  },
  { name: "Contact", path: "/contact", external: false },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState({});
  // Separate from the desktop dropdown so its click-outside handler doesn't close it
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState({});
  const location = useLocation();
  const dropdownRef = useRef(null);

  const toggleDropdown = (menuKey) =>
    setDropdownOpen((prev) => ({ ...prev, [menuKey]: !prev[menuKey] }));

  const closeDropdown = (menuKey) =>
    setDropdownOpen((prev) => ({ ...prev, [menuKey]: false }));

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Mark parent items active if current path matches any of their subitems
  const activeParents = useMemo(() => {
    const map = {};
    navigation.forEach((n) => {
      if (n.subItems) {
        map[n.name] = n.subItems.some(
          (s) => !s.external && location.pathname.startsWith(s.path)
        );
      }
    });
    return map;
  }, [location.pathname]);

  return (
    <>
      {/* Top info strip - Enhanced responsive design */}
      <div className="bg-black px-2 sm:px-4 py-2 sm:py-3 text-white text-xs sm:text-sm">
        <div className="mx-auto max-w-7xl">
          {/* Mobile: Stack vertically, center items */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 lg:gap-6 font-medium">
            {/* Mobile: Show only phone, hide others */}
            <span className="flex items-center gap-1 sm:gap-2">
              <FaLocationDot className="text-red-500 flex-shrink-0" size={16} />
              <span className="hidden sm:inline">San Jose, CA 95132</span>
              <span className="sm:hidden">San Jose, CA</span>
            </span>
            
            <span className="flex items-center gap-1 sm:gap-2">
              <FaPhone className="text-red-500 flex-shrink-0" size={16} />
              <a 
                href="tel:4083470402" 
                className="hover:text-gray-300 transition-colors"
              >
                (408) 347-0402
              </a>
            </span>
            
            {/* Hide email on very small screens */}
            <span className="hidden xs:flex items-center gap-1 sm:gap-2">
              <TbMailFilled className="text-red-500 flex-shrink-0" size={16} />
              <a 
                href="mailto:padproadult@yahoo.com" 
                className="hover:text-gray-300 transition-colors break-all sm:break-normal"
              >
                <span className="sm:hidden">Contact</span>
                <span className="hidden sm:inline">padproadult@yahoo.com</span>
              </a>
            </span>
          </div>
        </div>
      </div>

      <header className="sticky top-0 z-50 bg-white shadow-sm">
        <nav className="bg-[#f5efd6] border-b border-gray-200 relative">
          <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
            {/* Desktop Logo positioned absolutely - Enhanced positioning */}
            <div className="hidden lg:block absolute top-0 -translate-y-full mt-2 xl:mt-3 z-20 left-3 sm:left-6 lg:left-8 xl:left-16 pointer-events-none">
              <div className="bg-white rounded-b-2xl shadow-md ring-1 ring-black/5 pt-1 pb-2 px-2 pointer-events-auto">
                <Link to="/" className="block">
                  <div className="h-14 xl:h-16 w-[180px] xl:w-[220px] overflow-hidden">
                    <img
                      src={icon}
                      alt="Piedmont Adult Day Program"
                      className="block h-16 xl:h-20 w-auto pt-4 xl:pt-6 ps-2 xl:ps-3 -translate-y-2 xl:-translate-y-3 transition-transform duration-200 hover:scale-105"
                    />
                  </div>
                </Link>
              </div>
            </div>

            {/* Navigation container - Enhanced responsive layout */}
            <div className="flex items-center justify-between min-h-[64px] sm:min-h-[72px]">
              {/* Mobile logo - Enhanced sizing */}
              <div className="lg:hidden flex-shrink-0">
                <Link to="/" className="block">
                  <img
                    src={icon}
                    alt="Piedmont Adult Day Program"
                    className="h-10 sm:h-12 w-auto transition-transform duration-200 hover:scale-105"
                  />
                </Link>
              </div>

              {/* Desktop nav - Enhanced spacing and typography */}
              <div className="hidden lg:flex flex-1 justify-center items-center gap-x-6 xl:gap-x-10 pt-4 xl:pt-6 pb-3 xl:pb-4">
                {navigation.map((navItem) =>
                  navItem.subItems ? (
                    <div
                      key={navItem.name}
                      className="relative"
                      ref={dropdownRef}
                    >
                      <button
                        onClick={() => toggleDropdown(navItem.name)}
                        onMouseEnter={() => setDropdownOpen({ [navItem.name]: true })}
                        className={[
                          "relative pb-2 xl:pb-3 text-sm xl:text-base font-semibold text-gray-900 hover:text-gray-700 flex items-center gap-1 xl:gap-2 transition-colors duration-200",
                          activeParents[navItem.name]
                            ? "after:absolute after:left-0 after:bottom-0 after:h-1 after:w-full after:bg-green-600"
                            : "after:absolute after:left-0 after:bottom-0 after:h-1 after:w-0 after:bg-green-600 hover:after:w-full after:transition-all after:duration-300",
                        ].join(" ")}
                      >
                        {navItem.name}
                        {dropdownOpen[navItem.name] ? (
                          <ChevronUpIcon className="h-3 w-3 xl:h-4 xl:w-4" />
                        ) : (
                          <ChevronDownIcon className="h-3 w-3 xl:h-4 xl:w-4" />
                        )}
                      </button>

                      {/* Custom dropdown menu - Enhanced positioning */}
                      {dropdownOpen[navItem.name] && (
                        <div
                          className="absolute top-full left-0 mt-1 xl:mt-2 w-48 xl:w-56 bg-white border border-gray-200 rounded-md shadow-lg z-50"
                          onMouseLeave={() => closeDropdown(navItem.name)}
                        >
                          <div className="py-1">
                            {navItem.subItems.map((sub) =>
                              sub.external ? (
                                <a
                                  key={sub.name}
                                  href={sub.path}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block px-3 xl:px-4 py-2 text-sm xl:text-base text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-150"
                                  onClick={() => closeDropdown(navItem.name)}
                                >
                                  {sub.name}
                                </a>
                              ) : (
                                <Link
                                  key={sub.name}
                                  to={sub.path}
                                  className="block px-3 xl:px-4 py-2 text-sm xl:text-base text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-150"
                                  onClick={() => closeDropdown(navItem.name)}
                                >
                                  {sub.name}
                                </Link>
                              )
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : navItem.external ? (
                    <a
                      key={navItem.name}
                      href={navItem.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative pb-2 xl:pb-3 text-sm xl:text-base font-semibold text-gray-900 hover:text-gray-700 transition-colors duration-200"
                    >
                      {navItem.name}
                    </a>
                  ) : (
                    <NavLink
                      key={navItem.name}
                      to={navItem.path}
                      className={({ isActive }) =>
                        [
                          "relative pb-2 xl:pb-3 text-sm xl:text-base font-semibold text-gray-900 hover:text-gray-700 transition-colors duration-200",
                          isActive
                            ? "after:absolute after:left-0 after:bottom-0 after:h-1 after:w-full after:bg-green-600"
                            : "after:absolute after:left-0 after:bottom-0 after:h-1 after:w-0 after:bg-green-600 hover:after:w-full after:transition-all after:duration-300",
                        ].join(" ")
                      }
                    >
                      {navItem.name}
                    </NavLink>
                  )
                )}
              </div>

              {/* Mobile menu button - Enhanced touch target */}
              <div className="lg:hidden">
                <button
                  type="button"
                  className="-m-2 p-2 text-gray-700 hover:text-gray-900 transition-colors duration-200"
                  onClick={() => setMobileMenuOpen(true)}
                  aria-label="Open menu"
                >
                  <Bars3Icon className="h-6 w-6 sm:h-7 sm:w-7" />
                </button>
              </div>
            </div>
          </div>
        </nav>

        {/* Mobile menu */}
        <Dialog as="div" className="lg:hidden" open={mobileMenuOpen} onClose={setMobileMenuOpen}>
          <div className="fixed inset-0 z-50 bg-black/30" />
          <Dialog.Panel className="fixed inset-y-0 right-0 z-50 flex w-full flex-col overflow-y-auto bg-[#f5efd6] sm:max-w-sm">
            {/* Same bar as the closed header so the logo doesn't jump */}
            <div className="flex items-center justify-between min-h-[64px] sm:min-h-[72px] px-3 sm:px-6 border-b border-gray-200">
              <Link to="/" className="flex-shrink-0" onClick={() => setMobileMenuOpen(false)}>
                <img className="h-10 sm:h-12 w-auto" src={icon} alt="Piedmont Adult Day Program" />
              </Link>
              <button
                type="button"
                className="-m-2 p-2 text-gray-700 hover:text-gray-900 transition-colors duration-200"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
              >
                <XMarkIcon className="h-6 w-6 sm:h-7 sm:w-7" aria-hidden="true" />
              </button>
            </div>

            <nav className="flex-1 px-3 sm:px-6">
              {navigation.map((navItem) =>
                navItem.subItems ? (
                  <div key={navItem.name} className="border-b border-black/10">
                    <button
                      onClick={() =>
                        setMobileDropdownOpen((prev) => ({ ...prev, [navItem.name]: !prev[navItem.name] }))
                      }
                      className="flex w-full items-center justify-between py-4 text-left text-base font-semibold text-gray-900"
                    >
                      <span
                        className={
                          activeParents[navItem.name]
                            ? "relative pb-1 after:absolute after:left-0 after:bottom-0 after:h-1 after:w-full after:bg-green-600"
                            : ""
                        }
                      >
                        {navItem.name}
                      </span>
                      {mobileDropdownOpen[navItem.name] ? (
                        <ChevronUpIcon className="h-4 w-4 text-gray-700" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4 text-gray-700" />
                      )}
                    </button>

                    {mobileDropdownOpen[navItem.name] && (
                      <div className="pb-3 pl-4">
                        {navItem.subItems.map((sub) =>
                          sub.external ? (
                            <a
                              key={sub.name}
                              href={sub.path}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                              onClick={() => setMobileMenuOpen(false)}
                            >
                              {sub.name}
                            </a>
                          ) : (
                            <Link
                              key={sub.name}
                              to={sub.path}
                              className={`block py-2 text-sm font-medium hover:text-gray-900 ${
                                location.pathname.startsWith(sub.path) ? "text-green-700" : "text-gray-700"
                              }`}
                              onClick={() => setMobileMenuOpen(false)}
                            >
                              {sub.name}
                            </Link>
                          )
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={navItem.name}
                    to={navItem.path}
                    className="block border-b border-black/10 py-4 text-base font-semibold text-gray-900 hover:text-gray-700"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span
                      className={
                        location.pathname === navItem.path
                          ? "relative pb-1 after:absolute after:left-0 after:bottom-0 after:h-1 after:w-full after:bg-green-600"
                          : ""
                      }
                    >
                      {navItem.name}
                    </span>
                  </Link>
                )
              )}
            </nav>

            {/* Contact strip, styled like the black bar at the top of the site */}
            <div className="bg-black px-3 sm:px-6 py-5 text-sm font-medium text-white space-y-3">
              <a href="tel:4083470402" className="flex items-center gap-2 hover:text-gray-300 transition-colors">
                <FaPhone className="text-red-500 flex-shrink-0" size={16} />
                (408) 347-0402
              </a>
              <a href="mailto:padproadult@yahoo.com" className="flex items-center gap-2 hover:text-gray-300 transition-colors">
                <TbMailFilled className="text-red-500 flex-shrink-0" size={16} />
                <span className="break-all">padproadult@yahoo.com</span>
              </a>
              <span className="flex items-center gap-2">
                <FaLocationDot className="text-red-500 flex-shrink-0" size={16} />
                San Jose, CA 95132
              </span>
            </div>
          </Dialog.Panel>
        </Dialog>
      </header>
    </>
  );
};

export default Header;